import { create } from "zustand";
import { v4 as uuid4 } from "uuid";
import { debounce } from "lodash";
import { useMemo, useCallback } from "react";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";
import type { Message } from "@ai-sdk/ui-utils";
import { AiChatType } from "@src/types.generated";

export interface SelectedSession {
  id: string;
  title: string;
  createdAt: string;
  messages: Message[];
  artifact?: string;
  artifactLoading?: boolean;
  artifactMessageId?: string | null;
  streamingError?: Error | null;
}

interface SessionsState {
  cache: Map<string, AiChatFragment>;
  sessions: AiChatFragment[];
  loading: boolean;
  error?: Error;
}

interface ChatSidebarState {
  isOpen: boolean;
  conversationIds: Partial<Record<AiChatType, string>>;
  selectedSessions: Partial<Record<AiChatType, SelectedSession | null>>;
  sessionsStates: Partial<Record<AiChatType, SessionsState>>;
  initialQueries: Partial<Record<AiChatType, string | null>>;
  toggle: () => void;
  open: () => void;
  close: () => void;
  startNewConversation: (chatType: AiChatType) => void;
  selectSession: (session: SelectedSession, chatType: AiChatType) => void;
  setInitialQuery: (query: string, chatType: AiChatType) => void;
  clearInitialQuery: (chatType: AiChatType) => void;
  cacheSessions: (sessions: AiChatFragment[], chatType: AiChatType) => void;
  setSessionsLoading: (loading: boolean, chatType: AiChatType) => void;
  setSessionsError: (error: Error | undefined, chatType: AiChatType) => void;
  setArtifact: (artifact: string, chatType: AiChatType) => void;
  setArtifactLoading: (loading: boolean, chatType: AiChatType) => void;
  setArtifactMessageId: (
    messageId: string | null,
    chatType: AiChatType,
  ) => void;
  setStreamingError: (error: Error | null, chatType: AiChatType) => void;
}

const createDefaultSessionsState = (): SessionsState => ({
  cache: new Map(),
  sessions: [],
  loading: false,
  error: undefined,
});

const createNewConversationSession = () => {
  const conversationId = uuid4();
  const newSession: SelectedSession = {
    id: conversationId,
    title: "New Chat",
    createdAt: new Date().toISOString(),
    messages: [],
    artifact: "",
    artifactLoading: false,
    artifactMessageId: null,
    streamingError: null,
  };

  return { conversationId, newSession };
};

export const chatSidebarStore = create<ChatSidebarState>((set) => ({
  isOpen: false,
  conversationIds: {
    [AiChatType.CourseContent]: uuid4(),
    [AiChatType.CourseOutline]: uuid4(),
    [AiChatType.KnowledgeBase]: uuid4(),
    [AiChatType.ModuleOutline]: uuid4(),
    [AiChatType.ModuleContent]: uuid4(),
    [AiChatType.GuestFeedbackAnalysis]: uuid4(),
    [AiChatType.TaskListOutline]: uuid4(),
    [AiChatType.CheckInOutline]: uuid4(),
    [AiChatType.AuditOutline]: uuid4(),
  },
  selectedSessions: {
    [AiChatType.CourseContent]: null,
    [AiChatType.CourseOutline]: null,
    [AiChatType.KnowledgeBase]: null,
    [AiChatType.ModuleOutline]: null,
    [AiChatType.ModuleContent]: null,
    [AiChatType.GuestFeedbackAnalysis]: null,
    [AiChatType.TaskListOutline]: null,
    [AiChatType.CheckInOutline]: null,
    [AiChatType.AuditOutline]: null,
  },
  sessionsStates: {
    [AiChatType.CourseContent]: createDefaultSessionsState(),
    [AiChatType.CourseOutline]: createDefaultSessionsState(),
    [AiChatType.KnowledgeBase]: createDefaultSessionsState(),
    [AiChatType.ModuleOutline]: createDefaultSessionsState(),
    [AiChatType.ModuleContent]: createDefaultSessionsState(),
    [AiChatType.GuestFeedbackAnalysis]: createDefaultSessionsState(),
    [AiChatType.TaskListOutline]: createDefaultSessionsState(),
    [AiChatType.CheckInOutline]: createDefaultSessionsState(),
    [AiChatType.AuditOutline]: createDefaultSessionsState(),
  },
  initialQueries: {
    [AiChatType.CourseContent]: null,
    [AiChatType.CourseOutline]: null,
    [AiChatType.KnowledgeBase]: null,
    [AiChatType.ModuleOutline]: null,
    [AiChatType.ModuleContent]: null,
    [AiChatType.GuestFeedbackAnalysis]: null,
    [AiChatType.TaskListOutline]: null,
    [AiChatType.CheckInOutline]: null,
    [AiChatType.AuditOutline]: null,
  },
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  startNewConversation: (chatType: AiChatType) =>
    set((state) => {
      const { conversationId, newSession } = createNewConversationSession();

      return {
        conversationIds: {
          ...state.conversationIds,
          [chatType]: conversationId,
        },
        selectedSessions: {
          ...state.selectedSessions,
          [chatType]: newSession,
        },
        initialQueries: {
          ...state.initialQueries,
          [chatType]: null,
        },
      };
    }),
  selectSession: (session: SelectedSession, chatType: AiChatType) =>
    set((state) => ({
      conversationIds: {
        ...state.conversationIds,
        [chatType]: session.id,
      },
      selectedSessions: {
        ...state.selectedSessions,
        [chatType]: session,
      },
    })),
  setInitialQuery: (query: string, chatType: AiChatType) =>
    set((state) => ({
      initialQueries: {
        ...state.initialQueries,
        [chatType]: query,
      },
    })),
  clearInitialQuery: (chatType: AiChatType) =>
    set((state) => ({
      initialQueries: {
        ...state.initialQueries,
        [chatType]: null,
      },
    })),
  cacheSessions: (sessions: AiChatFragment[], chatType: AiChatType) =>
    set((state) => {
      const currentSessionsState = state.sessionsStates[chatType];

      if (!currentSessionsState) {
        return state;
      }

      const newCache = new Map(currentSessionsState.cache);

      sessions.forEach((session) => {
        newCache.set(session.id, session);
      });

      return {
        sessionsStates: {
          ...state.sessionsStates,
          [chatType]: {
            ...currentSessionsState,
            cache: newCache,
            sessions: sessions,
          },
        },
      };
    }),
  setSessionsLoading: (loading: boolean, chatType: AiChatType) =>
    set((state) => {
      const currentSessionsState = state.sessionsStates[chatType];

      if (!currentSessionsState) {
        return state;
      }

      return {
        sessionsStates: {
          ...state.sessionsStates,
          [chatType]: {
            ...currentSessionsState,
            loading,
          },
        },
      };
    }),
  setSessionsError: (error: Error | undefined, chatType: AiChatType) =>
    set((state) => {
      const currentSessionsState = state.sessionsStates[chatType];

      if (!currentSessionsState) {
        return state;
      }

      return {
        sessionsStates: {
          ...state.sessionsStates,
          [chatType]: {
            ...currentSessionsState,
            error,
          },
        },
      };
    }),
  setArtifact: (artifact: string, chatType: AiChatType) =>
    set((state) => {
      const currentSelectedSession = state.selectedSessions[chatType];

      if (!currentSelectedSession) return state;

      const updatedSession: SelectedSession = {
        ...currentSelectedSession,
        artifact,
      };

      return {
        selectedSessions: {
          ...state.selectedSessions,
          [chatType]: updatedSession,
        },
      };
    }),
  setArtifactLoading: (loading: boolean, chatType: AiChatType) =>
    set((state) => {
      const currentSelectedSession = state.selectedSessions[chatType];

      if (!currentSelectedSession) return state;

      const updatedSession: SelectedSession = {
        ...currentSelectedSession,
        artifactLoading: loading,
      };

      return {
        selectedSessions: {
          ...state.selectedSessions,
          [chatType]: updatedSession,
        },
      };
    }),
  setArtifactMessageId: (messageId: string | null, chatType: AiChatType) =>
    set((state) => {
      const currentSelectedSession = state.selectedSessions[chatType];

      if (!currentSelectedSession) return state;

      const updatedSession: SelectedSession = {
        ...currentSelectedSession,
        artifactMessageId: messageId,
      };

      return {
        selectedSessions: {
          ...state.selectedSessions,
          [chatType]: updatedSession,
        },
      };
    }),
  setStreamingError: (error: Error | null, chatType: AiChatType) =>
    set((state) => {
      const currentSelectedSession = state.selectedSessions[chatType];

      if (!currentSelectedSession) return state;

      const updatedSession: SelectedSession = {
        ...currentSelectedSession,
        streamingError: error,
      };

      return {
        selectedSessions: {
          ...state.selectedSessions,
          [chatType]: updatedSession,
        },
      };
    }),
}));

export interface UseChatSidebarReturn {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  conversationId: string;
  selectedSession: SelectedSession | null;
  sessions: SessionsState;
  initialQuery: string | null;
  startNewConversation: () => void;
  selectSession: (session: SelectedSession) => void;
  setInitialQuery: (query: string) => void;
  clearInitialQuery: () => void;
  cacheSessions: (sessions: AiChatFragment[]) => void;
  setSessionsLoading: (loading: boolean) => void;
  setSessionsError: (error: Error | undefined) => void;
  setArtifact: (artifact: string) => void;
  setArtifactLoading: (loading: boolean) => void;
  setArtifactMessageId: (messageId: string | null) => void;
  setStreamingError: (error: Error | null) => void;
}

export const useChatSidebar = (chatType: AiChatType): UseChatSidebarReturn => {
  const store = chatSidebarStore();

  // Ensure we have data for this chat type
  const conversationId = store.conversationIds[chatType];
  const selectedSession = store.selectedSessions[chatType];
  const sessions = store.sessionsStates[chatType];
  const initialQuery = store.initialQueries[chatType];

  if (!conversationId || !sessions) {
    throw new Error(`Chat type ${chatType} is not supported in the sidebar`);
  }

  const debouncedSetArtifact = useMemo(
    () =>
      debounce(
        (artifact: string) => store.setArtifact(artifact, chatType),
        150,
      ),
    [store, chatType],
  );

  const stableSetStreamingError = useCallback(
    (error: Error | null) => store.setStreamingError(error, chatType),
    [store, chatType],
  );

  return {
    isOpen: store.isOpen,
    toggle: store.toggle,
    open: store.open,
    close: store.close,
    conversationId,
    selectedSession: selectedSession ?? null,
    sessions,
    initialQuery: initialQuery ?? null,
    startNewConversation: () => store.startNewConversation(chatType),
    selectSession: (session: SelectedSession) =>
      store.selectSession(session, chatType),
    setInitialQuery: (query: string) => store.setInitialQuery(query, chatType),
    clearInitialQuery: () => store.clearInitialQuery(chatType),
    cacheSessions: (sessions: AiChatFragment[]) =>
      store.cacheSessions(sessions, chatType),
    setSessionsLoading: (loading: boolean) =>
      store.setSessionsLoading(loading, chatType),
    setSessionsError: (error: Error | undefined) =>
      store.setSessionsError(error, chatType),
    setArtifact: debouncedSetArtifact,
    setArtifactLoading: (loading: boolean) =>
      store.setArtifactLoading(loading, chatType),
    setArtifactMessageId: (messageId: string | null) =>
      store.setArtifactMessageId(messageId, chatType),
    setStreamingError: stableSetStreamingError,
  };
};

export type { SessionsState };
